import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { registryStatus } from '../../utilities/risk_registry';
import { getLocalTime } from '../../utilities/time';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { capatalizeFirstLetter } from '../../utilities';
import { getRoute, paths } from '../../constants/strings';

export const GetRiskSidebarOptions = ({ state, setState, readOnly }) => {
  const { t } = useTranslation();
  const { availableWorkspaces } = useWorkspace();
  const {
    status,
    creator,
    assignee,
    documentControllers,
    workspace,
    dateCreated,
    nextReviewDate,
    lastReviewDate,
    externalId,
  } = state || {};
  const options = [
    {
      title: t(keys.common.STATUS),
      editable: false,
      key: 'status',
      type: 'label',
      name: registryStatus[status]?.title,
      color: registryStatus[status]?.color,
    },
    {
      title: 'Last Reviewed',
      editable: !readOnly,
      key: 'lastReviewDate',
      type: 'date',
      date: lastReviewDate,
      value: lastReviewDate,
      onChange: ({ target: { value } }) =>
        setState((prev) => ({ ...prev, lastReviewDate: value || null })),
    },
    {
      title: t(keys.common.CREATOR),
      user: creator,
      editable: false,
      key: 'creator',
      type: 'user',
      text: getLocalTime(dateCreated).format('MMMM Do, YYYY'),
    },
    {
      title: 'Person Responsible',
      editable: !readOnly,
      key: 'assignee',
      type: 'user',
      selected: assignee,
      user: assignee,
      onSelectionChanged: (user) => {
        setState((prev) => ({ ...prev, assignee: user }));
      },
      onClose: () => setState((prev) => ({ ...prev, assignee: null })),
    },
    {
      title: 'Document Controllers',
      editable: !readOnly,
      key: 'documentControllers',
      type: 'multiuser',
      selected: documentControllers || [],
      stateUpdateOnly: true,
      disableSelectAll: true,
      onUserAdded: (user) =>
        setState((prev) => ({
          ...prev,
          documentControllers: [...(prev.documentControllers || []), user],
        })),
      onUserRemoved: (user) =>
        setState((prev) => ({
          ...prev,
          documentControllers: prev.documentControllers?.filter(
            ({ id }) => id !== user.id,
          ),
        })),
    },
    {
      title: t(keys.common.WORKSPACE),
      editable: !readOnly,
      key: 'workspace',
      type: 'workspace',
      selected: workspace,
      options: availableWorkspaces,
      onChange: (w) => setState((prev) => ({ ...prev, workspace: w || null })),
    },
    {
      title: 'Next Scheduled Review',
      editable: !readOnly,
      key: 'nextReviewDate',
      type: 'date',
      date: nextReviewDate,
      value: nextReviewDate,
      min: new Date(),
      onChange: ({ target: { value } }) =>
        setState((prev) => ({ ...prev, nextReviewDate: value || null })),
    },
    {
      title: 'ID',
      editable: !readOnly,
      key: 'externalId',
      type: 'text',
      value: !!externalId ? externalId : t(keys.common.NONE),
      onChange: (e) =>
        setState((prev) => ({ ...prev, externalId: e.target.value })),
    },
  ];
  return options;
};

export const SidebarButtons = ({
  readOnly,
  onEdit,
  onDelete,
  onUpdate,
  onReview,
  onCancel,
}) => {
  return [
    {
      value: 'Review',
      icon: 'grading',
      variant: 'success',
      show: !readOnly,
      onClick: onReview,
      outlined: false,
    },
    {
      value: 'Update',
      icon: 'edit',
      variant: 'primary',
      show: !readOnly,
      onClick: onUpdate,
      outlined: true,
    },
    {
      value: 'Cancel',
      icon: 'cancel',
      variant: 'secondary',
      show: !readOnly,
      onClick: onCancel,
      outlined: true,
    },
    {
      value: 'Edit',
      icon: 'edit',
      variant: 'warning',
      show: readOnly,
      onClick: onEdit,
      outlined: true,
    },
    {
      value: 'Delete',
      icon: 'delete',
      variant: 'danger',
      show: true,
      onClick: onDelete,
      outlined: true,
    },
  ];
};

const sanatizeVals = (vals) => ({
  ...vals,
  threats: [...(vals?.mitigations || []), ...(vals?.threats || [])].filter(
    ({ type }) => type === 'THREAT',
  ),
  barriers: [...(vals?.mitigations || []), ...(vals?.barriers || [])].filter(
    ({ type }) => type === 'BARRIER',
  ),
  consequences: [
    ...(vals?.mitigations || []),
    ...(vals?.consequences || []),
  ].filter(({ type }) => type === 'CONSEQUENCE'),
  policies: [...(vals?.mitigations || []), ...(vals?.policies || [])].filter(
    ({ type }) => type === 'POLICY',
  ),
  workspace: { id: vals?.workspace?.id },
  assignee: { id: vals?.assignee?.id },
  documentControllers: vals?.documentControllers?.map(
    ({ id, firstName, lastName }) => ({ id, firstName, lastName }),
  ),
});

export const handleRiskChange = (newVals, oldVals) => {
  const changes = {};
  const changedKeys = [];

  const sanatizedOldVals = sanatizeVals(oldVals);
  const sanatizedNewVals = sanatizeVals(newVals);

  Object.entries(newVals).forEach(([key, value]) => {
    if (oldVals[key] !== value) {
      changedKeys.push(key);
      switch (key) {
        case 'assignee':
          changes.assigneeId = value?.id || null;
          break;
        case 'workspace':
          changes.workspaceId = value?.id || null;
          break;
        case 'documentControllers':
          changes.documentControllerIds = value?.map(({ id }) => id) || [];
          break;
        case 'threats':
        case 'barriers':
        case 'consequences':
        case 'policies':
          changes.mitigations = [
            ...(changes?.mitigations || []),
            ...value?.map(({ value }) => value),
          ];
          break;
        default:
          changes[key] = value;
          break;
      }
    }
  });

  const log = Object.fromEntries(
    changedKeys.map((key) => [
      key,
      {
        old: sanatizedOldVals[key],
        new: sanatizedNewVals[key],
      },
    ]),
  );

  return { changes, changeLog: log };
};

function camelCaseToWords(str) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase());
}

export const generateChangeLogString = (log, key, lookups) => {
  const { new: newVals, old: oldVals } = log;
  const { users, workspaces, mitigations: existingMitigations } = lookups;

  let newString = '';
  let oldString = '';
  let keyString = key;

  switch (key) {
    case 'threats':
    case 'barriers':
    case 'consequences':
    case 'policies':
      newString =
        newVals?.length > 0
          ? newVals?.map((v) => v?.label ?? v?.title).join(', ')
          : 'none';
      oldString =
        oldVals?.length > 0
          ? oldVals?.map((v) => v?.label ?? v?.title).join(', ')
          : 'none';
      break;
    case 'assignee':
      const newUser = users.find(({ id }) => id === newVals?.id);
      newString = newUser
        ? newUser?.firstName + ' ' + newUser?.lastName
        : 'none';

      const oldUser = users.find(({ id }) => id === oldVals?.id);
      oldString = oldUser
        ? oldUser?.firstName + ' ' + oldUser?.lastName
        : 'none';
      break;
    case 'documentControllers':
      keyString = 'document controllers';
      newString =
        newVals?.length > 0
          ? newVals.map((u) => `${u?.firstName} ${u?.lastName}`).join(', ')
          : 'none';
      oldString =
        oldVals?.length > 0
          ? oldVals.map((u) => `${u?.firstName} ${u?.lastName}`).join(', ')
          : 'none';
      break;
    case 'workspace':
      const newWorkspace = workspaces.find(({ id }) => id === newVals?.id);
      newString = newWorkspace ? newWorkspace?.title : 'Company (Shared)';

      const oldWorkspace = workspaces.find(({ id }) => id === oldVals?.id);
      oldString = oldWorkspace ? oldWorkspace?.title : 'Company (Shared)';
      break;
    case 'residualImpact':
    case 'residualProbability':
      keyString = `mitigated ${
        key === 'residualImpact' ? 'impact' : 'probability'
      }`;
      oldString = oldVals ? oldVals : 'none';
      newString = newVals ? newVals : 'none';
      break;
    case 'impact':
    case 'probability':
      keyString = `mitigated ${key === 'impact' ? 'impact' : 'probability'}`;
      oldString = oldVals ? oldVals : 'none';
      newString = newVals ? newVals : 'none';
      break;
    case 'mitigationsToRemove':
      const mitigationsRemoved = newVals.map((id) =>
        existingMitigations?.find((m) => m?.id === id),
      );
      const typesRemoved = Array.from(
        new Set(mitigationsRemoved?.map((m) => m?.type)),
      );
      let supplementalStrings = [];

      for (const type of typesRemoved) {
        const mits = mitigationsRemoved
          .filter((m) => m?.type === type)
          ?.map((mit) => mit?.title)
          ?.join(', ');

        supplementalStrings.push(capatalizeFirstLetter(type) + `: ${mits}`);
      }
      return `Removed ${supplementalStrings?.join(', ')}`;
    default:
      keyString = camelCaseToWords(key);
      oldString = oldVals ? oldVals : 'none';
      newString = newVals ? newVals : 'none';
      break;
  }

  return `Changed ${keyString} from ${oldString} to ${newString}`;
};

export const handleRiskNavigate = ({
  setOpen,
  navigate,
  __typename,
  id,
  workspaceId,
}) => {
  switch (__typename) {
    case 'Incident':
      return navigate(`/incident/${id}?section=DETAILS`);
    case 'CorrectiveAction':
      return navigate(`/cpa/${id}`);
    case 'Risk':
      return setOpen(id);
    case 'Inspection':
      return navigate(getRoute(workspaceId, paths.assessment, id));
    default:
      return;
  }
};

// __typename is the underlying Sequelize model name (riskAssociation.modelType),
// which no longer matches what these record types are called in the UI —
// "inspection" was renamed to "assessment" for display, and companies can
// further override that display name per-workspace via useCurrentUser().
export const getRecordTypeName = ({
  __typename,
  incidentName,
  assessmentName,
}) => {
  switch (__typename) {
    case 'Inspection':
      return assessmentName || 'Assessment';
    case 'Incident':
      return incidentName || 'Incident';
    case 'CorrectiveAction':
      return 'CPA';
    default:
      return null;
  }
};
